import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { Toaster } from './components/ui/sonner';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import TransactionsPage from './pages/TransactionsPage';
import BudgetPage from './pages/BudgetPage';
import GoalsPage from './pages/GoalsPage';
import RecurringBillsPage from './pages/RecurringBillsPage';
import CategoriesPage from './pages/CategoriesPage';
import AnnualReportPage from './pages/AnnualReportPage';
import SettingsPage from './pages/SettingsPage';
import ImportPage from './pages/ImportPage';
import MonthlyDetailPage from './pages/MonthlyDetailPage';
import AdminPage from './pages/AdminPage';
import AdminFamiliesPage from './pages/AdminFamiliesPage';
import AdminFamilyDetailPage from './pages/AdminFamilyDetailPage';
import AdminAIProvidersPage from './pages/AdminAIProvidersPage';
import AdminMarketProvidersPage from './pages/AdminMarketProvidersPage';
import QuantHubPage from './pages/QuantHubPage';
import QuantLabPage from './pages/QuantLabPage';
import FamilyPage from './pages/FamilyPage';
import PersonalPage from './pages/PersonalPage';
import ByMemberPage from './pages/ByMemberPage';
import PortfoliosPage from './pages/PortfoliosPage';
import PortfolioDetailPage from './pages/PortfolioDetailPage';
import './App.css';

const Spinner = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <Spinner />;
  if (isAuthenticated) return <Navigate to="/" replace />;
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/transactions" element={<ProtectedRoute><TransactionsPage /></ProtectedRoute>} />
      <Route path="/budget" element={<ProtectedRoute><BudgetPage /></ProtectedRoute>} />
      <Route path="/goals" element={<ProtectedRoute><GoalsPage /></ProtectedRoute>} />
      <Route path="/recurring" element={<ProtectedRoute><RecurringBillsPage /></ProtectedRoute>} />
      <Route path="/categories" element={<ProtectedRoute><CategoriesPage /></ProtectedRoute>} />
      <Route path="/annual" element={<ProtectedRoute><AnnualReportPage /></ProtectedRoute>} />
      <Route path="/import" element={<ProtectedRoute><ImportPage /></ProtectedRoute>} />
      <Route path="/monthly/:year/:month" element={<ProtectedRoute><MonthlyDetailPage /></ProtectedRoute>} />
      <Route path="/family" element={<ProtectedRoute><FamilyPage /></ProtectedRoute>} />
      <Route path="/me" element={<ProtectedRoute><PersonalPage /></ProtectedRoute>} />
      <Route path="/by-member" element={<ProtectedRoute><ByMemberPage /></ProtectedRoute>} />
      <Route path="/portfolios" element={<ProtectedRoute><PortfoliosPage /></ProtectedRoute>} />
      <Route path="/portfolios/:id" element={<ProtectedRoute><PortfolioDetailPage /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
      <Route path="/admin/families" element={<ProtectedRoute><AdminFamiliesPage /></ProtectedRoute>} />
      <Route path="/admin/families/:familyId" element={<ProtectedRoute><AdminFamilyDetailPage /></ProtectedRoute>} />
      <Route path="/admin/ai-providers" element={<ProtectedRoute><AdminAIProvidersPage /></ProtectedRoute>} />
      <Route path="/admin/market-providers" element={<ProtectedRoute><AdminMarketProvidersPage /></ProtectedRoute>} />
      <Route path="/admin/quant" element={<ProtectedRoute><QuantHubPage /></ProtectedRoute>} />
      <Route path="/admin/quant/lab" element={<ProtectedRoute><QuantLabPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRoutes />
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
